import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Eye, Pencil, Trash2, Upload, Users, Wand2 } from 'lucide-react';
import {
  TableRowActionsMenu,
  TableRowActionsMenuDivider,
  TableRowActionsMenuItem,
} from '@/components/common/TableRowActionsMenu';
import { QueryErrorState } from '@/components/common/QueryErrorState';
import type { Company } from '@/types/company.types';
import type { CompanySetupStatus } from '@/types/setup-wizard.types';
import { Badge, Button, ConfirmDialog, Modal } from '@/components/ui';
import { PermissionGate } from '@/components/common/PermissionGate';
import { EmptyState } from '@/components/common/EmptyState';
import { cn } from '@/lib/cn';
import { formatDate } from '@/lib/formatters';
import { useDeleteCompany } from '../hooks/useCompanies';
import { EditCompanyForm } from './EditCompanyForm';

export interface CompanyListProps {
  companies: Company[];
  isLoading: boolean;
  error: Error | null;
  searchTerm?: string;
  /** Re-runs the query behind this list from the error state's retry button. */
  onRetry?: () => Promise<unknown> | void;
  setupStatusByCompanyId?: Map<string, CompanySetupStatus>;
  showSetupStatus?: boolean;
  showStartSetupAction?: boolean;
}

function statusVariant(status: string): 'success' | 'neutral' | 'warning' {
  if (status === 'active') return 'success';
  if (status === 'inactive') return 'neutral';
  return 'warning';
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function CompanyCardSkeleton(): React.ReactElement {
  return (
    <div className="animate-pulse rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
        <div className="h-6 w-40 rounded bg-stone-200 dark:bg-gray-700" />
      </div>
      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="flex items-center gap-4 px-4 py-3">
            <div className="h-9 w-9 rounded-lg bg-stone-200 dark:bg-gray-700" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-2/5 rounded bg-stone-200 dark:bg-gray-700" />
              <div className="h-3 w-1/3 rounded bg-stone-100 dark:bg-gray-700" />
            </div>
            <div className="h-8 w-20 rounded-full bg-stone-100 dark:bg-gray-700" />
          </div>
        ))}
      </div>
      <div className="border-t border-gray-200 px-4 py-3 dark:border-gray-700">
        <div className="mx-auto h-4 w-32 rounded bg-stone-100 dark:bg-gray-700" />
      </div>
    </div>
  );
}

function CompanyDescription(company: Company): string {
  if (company.code) return company.code;
  return 'Company record';
}

function setupVariant(
  state: CompanySetupStatus['progress']['state']
): 'success' | 'neutral' | 'warning' {
  if (state === 'ready') return 'success';
  if (state === 'not_started') return 'neutral';
  return 'warning';
}

function setupLabel(state: CompanySetupStatus['progress']['state']): string {
  if (state === 'ready') return 'Complete';
  if (state === 'not_started') return 'Not started';
  return 'In progress';
}

function CompanyMeta({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="min-w-0">
      <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500 xl:hidden">
        {label}
      </p>
      <div className="mt-0.5 truncate text-sm font-medium text-gray-700 dark:text-gray-200">
        {value}
      </div>
    </div>
  );
}

function CompaniesPanelFooter({ count }: { count: number }): React.ReactElement {
  return (
    <div className="flex items-center justify-center text-sm text-gray-600 dark:text-gray-300">
      Showing {count} {count === 1 ? 'company' : 'companies'}
    </div>
  );
}

function CompaniesPanel({
  companies,
  openCompanyActionsId,
  setOpenCompanyActionsId,
  setCompanyToDelete,
  setCompanyToEdit,
  deletePending,
  setupStatusByCompanyId,
  showSetupStatus,
}: {
  companies: Company[];
  openCompanyActionsId: string | null;
  setOpenCompanyActionsId: (id: string | null) => void;
  setCompanyToDelete: (company: Company | null) => void;
  setCompanyToEdit: (company: Company | null) => void;
  deletePending: boolean;
  setupStatusByCompanyId: Map<string, CompanySetupStatus>;
  showSetupStatus: boolean;
}): React.ReactElement {
  const navigate = useNavigate();

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-[0_1px_3px_rgba(15,23,42,0.06)] dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
        <div>
          <h2 className="text-sm font-semibold text-gray-950 dark:text-gray-100">All companies</h2>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            Browse every company record available in your current scope.
          </p>
        </div>
      </div>

      <div className="hidden grid-cols-[minmax(240px,1.4fr)_minmax(100px,.55fr)_minmax(120px,.65fr)_minmax(120px,.7fr)_minmax(100px,.5fr)_52px] gap-4 border-b border-gray-200 bg-gray-50/70 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 xl:grid dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-400">
        <span>Company</span>
        <span>Code</span>
        <span>Setup</span>
        <span>Updated</span>
        <span>Members</span>
        <span />
      </div>

      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        {companies.map((company) => {
          const showMemberCount = company.memberCount != null;
          const setupStatus = setupStatusByCompanyId.get(company._id);
          const updatedLabel = company.updatedAt
            ? formatDate(company.updatedAt, 'dd MMM yyyy')
            : company.createdAt
              ? formatDate(company.createdAt, 'dd MMM yyyy')
              : 'Not available';

          return (
            <div
              key={company._id}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/companies/${company._id}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(`/companies/${company._id}`);
                }
              }}
              aria-label={`Open ${company.name}`}
              className={cn(
                'group flex cursor-pointer flex-col gap-3 px-4 py-3 transition-colors',
                'hover:bg-primary/5 dark:hover:bg-primary/10',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset',
                'xl:grid xl:grid-cols-[minmax(240px,1.4fr)_minmax(100px,.55fr)_minmax(120px,.65fr)_minmax(120px,.7fr)_minmax(100px,.5fr)_52px] xl:items-center xl:gap-4'
              )}
            >
              <div className="flex min-w-0 items-center gap-3">
                <div
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-xs font-bold text-primary dark:bg-primary/20 dark:text-blue-200"
                  aria-hidden
                >
                  {initialsOf(company.name)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-center gap-3">
                    <h3 className="truncate text-sm font-semibold text-gray-950 transition-colors group-hover:text-primary dark:text-gray-100">
                      {company.name}
                    </h3>
                    <Badge variant={statusVariant(company.status)} className="capitalize">
                      {company.status}
                    </Badge>
                  </div>
                  <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
                    {CompanyDescription(company)}
                  </p>
                </div>
              </div>

              <div className="contents">
                <CompanyMeta
                  label="Code"
                  value={
                    company.code ? <span className="font-mono">{company.code}</span> : 'Not set'
                  }
                />
                <CompanyMeta
                  label="Setup"
                  value={
                    showSetupStatus && setupStatus ? (
                      <span className="inline-flex items-center gap-2">
                        <Badge variant={setupVariant(setupStatus.progress.state)}>
                          {setupLabel(setupStatus.progress.state)}
                        </Badge>
                        {setupStatus.progress.state !== 'ready' ? (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {setupStatus.progress.completed}/{setupStatus.progress.total}
                          </span>
                        ) : null}
                      </span>
                    ) : (
                      'N/A'
                    )
                  }
                />
                <CompanyMeta label="Updated" value={updatedLabel} />
                <CompanyMeta
                  label="Members"
                  value={
                    showMemberCount ? (
                      <span className="inline-flex items-center gap-1.5">
                        <Users className="h-3.5 w-3.5" aria-hidden />
                        {company.memberCount}
                      </span>
                    ) : (
                      'N/A'
                    )
                  }
                />
              </div>

              <div
                className="flex items-center justify-end"
                aria-label={`Actions for ${company.name}`}
              >
                <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                  <TableRowActionsMenu
                    rowId={company._id}
                    openRowId={openCompanyActionsId}
                    onOpenRowIdChange={setOpenCompanyActionsId}
                    triggerAriaLabel={`Actions for ${company.name}`}
                    menuAriaLabel="Company actions"
                  >
                    <TableRowActionsMenuItem
                      icon={<Eye className="h-4 w-4" aria-hidden />}
                      onSelect={() => navigate(`/companies/${company._id}`)}
                    >
                      View
                    </TableRowActionsMenuItem>
                    <PermissionGate module="import_setup" action="read">
                      <>
                        {setupStatus?.progress.state !== 'ready' ? (
                          <TableRowActionsMenuItem
                            icon={<Wand2 className="h-4 w-4" aria-hidden />}
                            onSelect={() => navigate(`/companies/${company._id}/setup-wizard`)}
                          >
                            Continue setup
                          </TableRowActionsMenuItem>
                        ) : null}
                        <TableRowActionsMenuItem
                          icon={<Upload className="h-4 w-4" aria-hidden />}
                          onSelect={() => navigate(`/companies/${company._id}/setup-wizard#import`)}
                        >
                          Add members
                        </TableRowActionsMenuItem>
                      </>
                    </PermissionGate>
                    <PermissionGate module="companies" action="update">
                      <TableRowActionsMenuItem
                        icon={<Pencil className="h-4 w-4" aria-hidden />}
                        onSelect={() => setCompanyToEdit(company)}
                      >
                        Edit
                      </TableRowActionsMenuItem>
                    </PermissionGate>
                    <PermissionGate module="companies" action="delete">
                      <>
                        <TableRowActionsMenuDivider />
                        <TableRowActionsMenuItem
                          variant="destructive"
                          icon={<Trash2 className="h-4 w-4" aria-hidden />}
                          onSelect={() => setCompanyToDelete(company)}
                          disabled={deletePending}
                        >
                          Delete
                        </TableRowActionsMenuItem>
                      </>
                    </PermissionGate>
                  </TableRowActionsMenu>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="border-t border-gray-200 px-4 py-3 dark:border-gray-700">
        <CompaniesPanelFooter count={companies.length} />
      </div>
    </div>
  );
}

export function CompanyList({
  companies,
  isLoading,
  error,
  onRetry,
  searchTerm = '',
  setupStatusByCompanyId = new Map(),
  showSetupStatus = false,
  showStartSetupAction = false,
}: CompanyListProps): React.ReactElement {
  const navigate = useNavigate();
  const [openCompanyActionsId, setOpenCompanyActionsId] = useState<string | null>(null);
  const [companyToEdit, setCompanyToEdit] = useState<Company | null>(null);
  const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null);
  const deleteCompany = useDeleteCompany();

  const handleConfirmDelete = (): void => {
    if (companyToDelete) {
      deleteCompany.mutate(companyToDelete._id);
      setCompanyToDelete(null);
    }
  };

  if (isLoading) {
    return <CompanyCardSkeleton />;
  }

  if (error) {
    return <QueryErrorState error={error} title="Couldn't load companies" onRetry={onRetry} />;
  }

  if (companies.length === 0) {
    const hasSearch = searchTerm.trim().length > 0;
    return (
      <EmptyState
        icon={Building2}
        title={hasSearch ? 'No companies match your search' : 'No companies yet'}
        description={
          hasSearch
            ? 'Try a different company name or code.'
            : 'Start setup to create a company and import its first members.'
        }
        action={
          !hasSearch && showStartSetupAction ? (
            <Button type="button" size="sm" onClick={() => navigate('/companies/setup-wizard/drafts/new')}>
              <Wand2 className="mr-2 h-4 w-4" aria-hidden />
              Start setup
            </Button>
          ) : undefined
        }
      />
    );
  }

  return (
    <>
      <div>
        <CompaniesPanel
          companies={companies}
          openCompanyActionsId={openCompanyActionsId}
          setOpenCompanyActionsId={setOpenCompanyActionsId}
          setCompanyToDelete={setCompanyToDelete}
          setCompanyToEdit={setCompanyToEdit}
          deletePending={deleteCompany.isPending || !!companyToDelete}
          setupStatusByCompanyId={setupStatusByCompanyId}
          showSetupStatus={showSetupStatus}
        />
      </div>

      <Modal
        open={companyToEdit !== null}
        onClose={() => setCompanyToEdit(null)}
        title={companyToEdit ? `Edit ${companyToEdit.name}` : ''}
        className="max-w-lg"
      >
        {companyToEdit && (
          <EditCompanyForm
            company={companyToEdit}
            onSuccess={() => setCompanyToEdit(null)}
            onCancel={() => setCompanyToEdit(null)}
          />
        )}
      </Modal>

      <ConfirmDialog
        open={companyToDelete !== null}
        onClose={() => setCompanyToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Delete company"
        message={
          companyToDelete ? `Delete company "${companyToDelete.name}"? This cannot be undone.` : ''
        }
        confirmLabel="Delete"
        variant="danger"
        confirmLoading={deleteCompany.isPending}
      />
    </>
  );
}

CompanyList.displayName = 'CompanyList';
